import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { getFirebaseAuth, getFirebaseDb } from '../firebase/config';
import { COL } from '../firebase/collections';

const AuthContext = createContext();

export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  HR: 'hr',
  MENTOR: 'mentor',
  FINANCE: 'finance',
  EMPLOYEE: 'employee',
};

export const PERMISSIONS = {
  canCreateProject:     ['super_admin', 'admin', 'mentor'],
  canEditProject:       ['super_admin', 'admin', 'mentor'],
  canDeleteProject:     ['super_admin'],
  canAssignEmployee:    ['super_admin', 'mentor'],
  canViewAllProjects:   ['super_admin', 'admin', 'mentor', 'finance', 'hr'],
  canAddEmployee:       ['super_admin', 'hr', 'admin'],
  canEditEmployee:      ['super_admin', 'hr'],
  canDeleteEmployee:    ['super_admin'],
  canViewAllEmployees:  ['super_admin', 'admin', 'hr', 'mentor'],
  canManageRecruitment: ['super_admin', 'hr'],
  canPostJob:           ['super_admin', 'hr'],
  canShortlist:         ['super_admin', 'hr'],
  canScheduleInterview: ['super_admin', 'hr'],
  canHireApplicant:     ['super_admin', 'hr'],
  canViewFinance:       ['super_admin', 'finance', 'admin'],
  canCreateInvoice:     ['super_admin', 'finance'],
  canApproveInvoice:    ['super_admin'],
  canExportReports:     ['super_admin', 'finance'],
  canViewClients:       ['super_admin', 'admin', 'finance', 'mentor'],
  canViewContacts:      ['super_admin', 'admin', 'hr'],
  canAddClient:         ['super_admin', 'admin'],
  canEditClient:        ['super_admin', 'admin'],
  canDeleteClient:      ['super_admin'],
  canGenerateDoc:       ['super_admin', 'finance', 'hr'],
  canSignDoc:           ['super_admin'],
  canScheduleMeeting:   ['super_admin', 'admin', 'mentor', 'hr'],
  canViewMeetings:      ['super_admin', 'admin', 'mentor', 'hr', 'employee', 'finance'],
  canManageWebsite:     ['super_admin', 'admin'],
  canViewAllAttendance: ['super_admin', 'admin', 'hr'],
  canManageSettings:    ['super_admin'],
  canAccessChat:        ['super_admin', 'admin', 'mentor', 'employee', 'hr', 'finance'],
  canDeleteAnything:    ['super_admin'],
  canEditAnything:      ['super_admin'],
};

export function hasPermission(userRole, action) {
  return PERMISSIONS[action]?.includes(userRole) ?? false;
}

function mapAuthError(err) {
  const code = err?.code;
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
    return 'Invalid email or password';
  }
  if (code === 'auth/too-many-requests') return 'Too many attempts. Try again later.';
  if (code === 'auth/network-request-failed') return 'Network error. Check your connection.';
  return err?.message || 'Sign-in failed';
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const auth = getFirebaseAuth();
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      setAuthError(null);
      if (!fbUser) {
        if (!cancelled) {
          setUser(null);
          setAuthReady(true);
        }
        return;
      }
      try {
        const db = getFirebaseDb();
        const snap = await getDoc(doc(db, COL.USERS, fbUser.uid));
        if (!snap.exists()) {
          await signOut(auth);
          if (!cancelled) {
            setUser(null);
            setAuthReady(true);
            setAuthError('No user profile in Firestore. Run the seed script and ensure users/{uid} exists.');
          }
          return;
        }
        const p = snap.data();
        if (!cancelled) {
          setUser({
            uid: fbUser.uid,
            email: fbUser.email,
            name: p.displayName || fbUser.displayName || fbUser.email,
            role: p.role,
            employeeId: p.employeeId ?? null,
            dept: p.dept,
            initials: p.initials,
            color: p.color,
            title: p.title,
          });
          setAuthReady(true);
        }
      } catch (e) {
        if (!cancelled) {
          setUser(null);
          setAuthReady(true);
          setAuthError(e.message || 'Failed to load profile');
        }
      }
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  const login = async (email, password) => {
    try {
      const auth = getFirebaseAuth();
      await signInWithEmailAndPassword(auth, email.trim(), password);
      return { success: true };
    } catch (e) {
      return { success: false, error: mapAuthError(e) };
    }
  };

  const logout = async () => {
    await signOut(getFirebaseAuth());
    setUser(null);
  };

  const can = (action) => hasPermission(user?.role, action);

  const value = useMemo(() => ({
    user,
    authReady,
    authError,
    login,
    logout,
    can,
    isFounder:  user?.role === 'super_admin',
    isMentor:   user?.role === 'mentor',
    isHR:       user?.role === 'hr',
    isFinance:  user?.role === 'finance',
    isAdmin:    user?.role === 'admin',
    isEmployee: user?.role === 'employee',
    role:       user?.role,
  }), [user, authReady, authError]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
